
import React, { useEffect, useState } from 'react';
import Admin from '@/pages/Admin';
import { useDatabaseInitialization } from '@/hooks/useDatabaseInitialization';
import { useAdminAuthentication } from '@/hooks/useAdminAuthentication';
import { useConnectionRetry } from '@/hooks/useConnectionRetry';
import AdminLoadingSkeleton from '@/components/admin/AdminLoadingSkeleton';
import ConnectionErrorAlert from '@/components/admin/ConnectionErrorAlert';

const AdminProtected = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  // Custom hooks
  const { 
    isInitialized, 
    isLoading: dbInitLoading, 
    connectionError: dbConnectionError, 
    setConnectionError: setDbConnectionError,
    initializeDatabase 
  } = useDatabaseInitialization();
  
  const { checkAuth } = useAdminAuthentication();
  
  const { 
    handleRetryConnection,
    isLoading: connectionRetryLoading,
    connectionError: retryConnectionError,
    setConnectionError: setRetryConnectionError
  } = useConnectionRetry();

  // Combined state
  const isLoading = dbInitLoading || connectionRetryLoading;
  const connectionError = dbConnectionError || retryConnectionError;
  
  // Initialize database
  useEffect(() => {
    let mounted = true;
    
    setTimeout(() => {
      if (mounted) {
        initializeDatabase();
      }
    }, 300);
    
    return () => {
      mounted = false;
    };
  }, [initializeDatabase]);

  // Check authentication after initialization
  useEffect(() => {
    if (!isInitialized) return;
    
    let mounted = true;
    
    const timer = setTimeout(() => {
      if (!mounted) return;
      
      const authResult = checkAuth();
      if (mounted) {
        if (!authResult) {
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      }
    }, 500);
    
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [checkAuth, isInitialized]);

  // Function to continue offline
  const handleContinueOffline = () => {
    setDbConnectionError(null);
    setRetryConnectionError(null);
    setIsAuthenticated(true);
  };

  // Render based on current state
  if (connectionError && isInitialized && !isLoading) {
    return (
      <div className="container mx-auto p-4 space-y-8">
        <ConnectionErrorAlert 
          connectionError={connectionError}
          onRetryConnection={handleRetryConnection}
          onContinueOffline={handleContinueOffline}
        />
        <AdminLoadingSkeleton />
      </div>
    );
  }

  if (isLoading || !isInitialized || isAuthenticated === null) {
    return <AdminLoadingSkeleton />;
  }

  if (isAuthenticated) {
    return <Admin />;
  }
  
  return null;
};

export default React.memo(AdminProtected);
